// The HTTP client. Same origin, cookie session, JSON in and out.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
  }
}

export class OfflineError extends Error {}

async function request<T>(method: string, path: string, body?: unknown, keepalive = false): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      keepalive,
    });
  } catch (e) {
    throw new OfflineError(String(e));
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }
  if (!response.ok) {
    const detail =
      parsed && typeof parsed === "object" && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    const message = typeof detail === "string" ? detail : `${response.status} ${response.statusText}`;
    throw new ApiError(response.status, message, detail);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown, keepalive = false) => request<T>("POST", path, body, keepalive),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

export type Tier = "review" | "small";

export interface AvailableModel {
  id: string;
  label: string;
  provider: string;
  /** Whether this deployment holds the API key this model needs. */
  configured: boolean;
  /** Whether it can search the web in a way that keeps the citation rule. */
  searches: boolean;
  tiers: Tier[];
}

/** Where this month stands against the ceiling (design §12.3). Reported
 *  whether or not there is one: the number is information the author is
 *  entitled to, and only the notice depends on the ceiling. */
export interface BudgetState {
  spent_this_month_usd: number;
  ceiling_usd: number | null;
  over: boolean;
}

export interface UsageRow {
  name: string;
  runs: number;
  cost_usd: number;
  month_runs: number;
  month_cost_usd: number;
  prompt_tokens?: number;
  output_tokens?: number;
}

export interface Usage {
  lifetime_cost_usd: number;
  month_cost_usd: number;
  month_started: string;
  budget: { ceiling_usd: number | null; over: boolean };
  by_model: UsageRow[];
  by_kind: UsageRow[];
  by_document: UsageRow[];
}

/** The deployment's hard ceilings. Unlike the budget, these stop every run,
 *  asked for or not: the API keys are the deployment's, not the author's. */
export interface Limits {
  daily_usd: number;
  monthly_usd: number;
  spent_today_usd: number;
  spent_this_month_usd: number;
  /** This account is exempt, which is how the deployment's owner is set up. */
  unlimited: boolean;
}

export interface Me {
  id: string;
  email: string;
  default_search_enabled: boolean;
  background_enabled: boolean;
  monthly_budget_usd: number | null;
  budget: BudgetState;
  limits: Limits;
  /** Per-tier override; null means "leave it to the deployment". */
  model_overrides: Record<Tier, string | null>;
  model_defaults: Record<Tier, string>;
  available_models: AvailableModel[];
}

export interface DocumentSummary {
  id: string;
  title: string;
  preview: string;
  /** What the evaluation says this document is about; empty until one runs. */
  subject: string;
  updated_at: string;
  rev: number;
}

export interface ServerDocument {
  id: string;
  title: string;
  rev: number;
  search_enabled: boolean;
  /** Null until the web-search note has been shown for this document (§9.2). */
  search_notice_shown_at: string | null;
  order: string[];
  paragraphs: Record<string, { text: string; content_hash: string }>;
  updated_at: string;
}
